import React from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { WaveDivider } from "@/components/decor";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const OWNER = <span className="ml-2 text-xs font-semibold text-[#A78BFA] bg-[#A78BFA]/10 rounded-full px-2 py-0.5">To be confirmed by owner</span>;

const GROUPS = [
  { title: "Ordering", items: [
    ["How do I place an order?", "Add your favourite goodies to your bag, then head to checkout. You can order as a guest or create an account to track your orders."],
    ["Can I change or cancel my order?", <>If your order hasn't shipped yet, please <Link to="/contact" className="text-[#4CC9F0] underline">contact us</Link> as soon as possible and we'll do our best to help. {OWNER}</>],
  ]},
  { title: "Shipping", items: [
    ["Where do you ship?", <>We ship from Australia 🇦🇺 with international options available at checkout. {OWNER}</>],
    ["How much is shipping and how long does it take?", <>Shipping costs and delivery timeframes are calculated at checkout based on your address. Exact rates and timings will be confirmed. {OWNER}</>],
  ]},
  { title: "Returns", items: [
    ["What is your returns policy?", <>Please see our <Link to="/legal/refund-policy" className="text-[#4CC9F0] underline">Refund Policy</Link>. Full return windows and conditions will be confirmed. {OWNER}</>],
  ]},
  { title: "Books", items: [
    ["What age are the Chewie & Solo books for?", "The Great Biscuit Bonanza is a read-aloud picture book best enjoyed by children aged around 3–8 (and grown-ups who like biscuits)."],
    ["Will there be more books?", "Yes! More Chewie & Solo Adventures are on the way. Join the Pack to be the first to know."],
  ]},
  { title: "Clothing", items: [
    ["How do your clothing sizes run?", <>See our <Link to="/size-guide" className="text-[#4CC9F0] underline">Size Guide</Link> for detailed measurements for women's, men's and kids' clothing.</>],
    ["What are your garments made from?", "Materials are listed on each product page. We choose soft, comfortable fabrics wherever possible."],
  ]},
  { title: "Dog Clothing", items: [
    ["How do I choose the right size for my dog?", <>Always measure your dog rather than relying on breed alone. See our <Link to="/size-guide" className="text-[#4CC9F0] underline">Dog Size Guide</Link> for how to measure neck, chest and back length.</>],
  ]},
  { title: "Sizing", items: [
    ["Where can I find measurements?", <>Detailed measurements are in our <Link to="/size-guide" className="text-[#4CC9F0] underline">Size Guide</Link>, with tabs for Women's, Men's, Kids and Dogs.</>],
  ]},
  { title: "Transfers", items: [
    ["How do I apply an iron-on transfer?", <>Each transfer comes with heat-application instructions. General guidance will be confirmed. {OWNER}</>],
    ["Are your stickers and decals weatherproof?", "Our vinyl stickers and decals are designed to be durable. Specific care details are on each product page."],
  ]},
  { title: "International Orders", items: [
    ["Do you ship internationally?", <>International shipping options appear at checkout where available. Duties and taxes may apply depending on your country. {OWNER}</>],
  ]},
  { title: "Contact", items: [
    ["How do I get in touch?", <>We'd love to hear from you — head to our <Link to="/contact" className="text-[#4CC9F0] underline">Contact page</Link>.</>],
  ]},
];

export default function FAQ() {
  useSeo("FAQ — Frequently Asked Questions", "Answers about ordering, shipping, returns, books, clothing, dog sizing and more.");
  return (
    <div>
      <section className="relative bg-[#FFD93D]/25 py-14 sm:py-18 text-center overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <span className="font-hand text-2xl text-[#FF6B9D]">Got questions?</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#19324A] mt-1">Frequently Asked Questions</h1>
        </div>
        <WaveDivider color="#FFF9EE" />
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-8">
        {GROUPS.map((g) => (
          <div key={g.title}>
            <h2 className="font-display font-bold text-2xl text-[#19324A] mb-3">{g.title}</h2>
            <Accordion type="single" collapsible className="bg-white rounded-3xl soft-shadow px-5">
              {g.items.map(([q, a], i) => (
                <AccordionItem key={i} value={`${g.title}-${i}`} className="border-[#19324A]/8">
                  <AccordionTrigger className="font-display font-semibold text-[#19324A] text-left hover:no-underline">{q}</AccordionTrigger>
                  <AccordionContent className="text-[#19324A]/75">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
        <p className="text-center text-sm text-[#19324A]/50">Items marked “To be confirmed by owner” are placeholders pending final policy details.</p>
      </section>
    </div>
  );
}
